import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { setIn } from 'final-form';
import isEqual from 'lodash/isEqual';
import { useLocation } from 'react-router-dom';
import api from 'api';
import { Listing } from 'models/Listing';
import { EDIT_ROUTE } from 'data/global';
import utils, { checkIsUrgent, sentryError } from 'utils';
import {
  LCW_PREVIEW_MODE_ROUTE,
  processLCWValuesBeforeSubmit,
} from '../../utils';
import ListingContext from '../Listing/Listing.context';
import ListingEditContext from '../../subrouters/EditListingSubrouter/providers/Listing/Listing.context';
import PatchListingContext from '../PatchListing/PatchListing.context';
import SaveStatusContext from '../SaveStatus/SaveStatus.context';
import PreviewPatchListingContext, {
  SaveFieldsOptions,
} from './PreviewPatchListing.context';

export const PREVIEW_SAVE_DEBOUNCE_TIME = 1000;

/**
 * Sent with every preview-mode patch. The create flow resumes on
 * LCW_ROUTE_PATHS[currentStep] and preview mode is not one of those steps, so
 * the field has to name a real one - the first, where the wizard starts.
 */
const PREVIEW_CURRENT_STEP = 0;

/**
 * '', null and undefined all mean "no value", and so does 0 for the money
 * fields, since an emptied money input is stored as 0
 * (getIntegerFromDottedNumber('') === 0). Preview mode edits no field where a
 * real 0 is meaningful.
 */
const isEmptyValue = (value: any) =>
  value === '' || value === null || value === undefined || value === 0;

const isUnchanged = (next: any, current: any) =>
  isEmptyValue(next) && isEmptyValue(current) ? true : isEqual(next, current);

/**
 * Emptying one of these in preview mode leaves the saved value alone rather
 * than clearing it - the listing is already published, and a blank title or
 * description on the public page is worse than a stale one. Clearing them is
 * done in form view, which submits the whole step.
 *
 * Edit mode only: a listing being created has nothing public to protect, so
 * there every field still saves exactly as typed.
 */
const EDIT_KEPT_WHEN_EMPTIED: string[] = [
  'listingTitle',
  'businessDescription',
];

export type PreviewPatchListingProviderProps = {
  children?: React.ReactNode;
};

const PreviewPatchListingProvider: React.FC<
  PreviewPatchListingProviderProps
> = (props) => {
  const { children } = props;

  const { pathname } = useLocation();
  const isEdit = pathname.includes(EDIT_ROUTE);
  const isPreviewMode = pathname.includes(LCW_PREVIEW_MODE_ROUTE);

  const listingCreateContext = useContext(ListingContext);
  const listingEditContext = useContext(ListingEditContext);

  const { listing, listingType, setListing } = isEdit
    ? listingEditContext
    : listingCreateContext;

  const { notifySaving, notifySaved, notifyFailed, registerRetry } =
    useContext(SaveStatusContext);

  /** Coalesced changes waiting to be sent. Last write wins per key. */
  const pendingRef = useRef<Record<string, any>>({});
  /** Payload of the last failed request, kept so retry can re-send it. */
  const failedRef = useRef<Record<string, any>>({});
  const inFlightRef = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(null);

  // Read through refs so `flush` stays referentially stable across renders.
  const listingRef = useRef(listing);
  listingRef.current = listing;
  const listingTypeRef = useRef(listingType);
  listingTypeRef.current = listingType;

  /**
   * `type` is otherwise only set by a step 0 form submit, so a listing that is
   * previewed and edited before that step would never get one.
   */
  const getBootstrapPayload = useCallback(() => {
    const currentListing = listingRef.current;

    if (!currentListing || currentListing.stepsCompleted !== 0) return {};

    return {
      type: listingTypeRef.current,
      ...(checkIsUrgent(listingTypeRef.current) &&
      currentListing.appointmentType
        ? { appointmentType: currentListing.appointmentType }
        : {}),
    };
  }, []);

  /**
   * Expands dotted field names against the current listing so sibling keys of a
   * nested object survive, then runs the shared LCW value processing.
   */
  const toListingShape = useCallback((values: Record<string, any>) => {
    const currentListing = listingRef.current || {};

    const expanded = Object.entries(values).reduce((acc, [key, value]) => {
      if (!key.includes('.')) return { ...acc, [key]: value };

      const [root, ...rest] = key.split('.');
      const base = { ...((currentListing as any)[root] || {}) };

      return { ...acc, [root]: setIn(base, rest.join('.'), value) };
    }, {});

    return processLCWValuesBeforeSubmit(expanded);
  }, []);

  /**
   * Drops keys that already hold the incoming value, so merely opening and
   * closing a field never patches, plus the emptied ones the edit flow keeps
   * (see EDIT_KEPT_WHEN_EMPTIED). Fields save themselves from several places
   * (DetailPageField, ListingSlugField's blur, the financials editor), so this
   * has to be the single gate rather than a check at each call site.
   */
  const pickChangedValues = useCallback(
    (patch: Record<string, any>) => {
      const currentListing = (listingRef.current || {}) as Record<string, any>;

      return Object.entries(patch).reduce((acc, [key, value]) => {
        if (isUnchanged(value, currentListing[key])) return acc;

        if (
          isEdit &&
          isEmptyValue(value) &&
          EDIT_KEPT_WHEN_EMPTIED.includes(key)
        )
          return acc;

        return { ...acc, [key]: value };
      }, {});
    },
    [isEdit],
  );

  const flush: () => Promise<void> = useCallback(async () => {
    if (inFlightRef.current) return;

    const listingId = listingRef.current?._id;
    if (!listingId) return;

    // Edits made after a failure win per key; untouched failed keys still ride
    // along so a retry re-sends everything outstanding.
    const changes = { ...failedRef.current, ...pendingRef.current };

    // Checked before the bootstrap keys and the step are added, so neither
    // triggers a request on its own.
    if (!Object.keys(changes).length) return;

    const payload = {
      ...changes,
      ...getBootstrapPayload(),
      currentStep: PREVIEW_CURRENT_STEP,
    };

    pendingRef.current = {};
    failedRef.current = {};
    inFlightRef.current = true;

    notifySaving();
    // Optimistic, and deliberately before the request: the typed value must
    // survive in the draft whether or not the save succeeds.
    setListing((old: any) => ({ ...old, ...payload }));

    try {
      await api.listings.patchListing(listingId, payload as Partial<Listing>);

      inFlightRef.current = false;

      // Drain anything that arrived while this request was in flight.
      if (Object.keys(pendingRef.current).length) return flush();

      registerRetry(null);
      notifySaved();
    } catch (e) {
      inFlightRef.current = false;
      sentryError(e);

      failedRef.current = payload;
      registerRetry(flush);
      notifyFailed();
    }
  }, [
    getBootstrapPayload,
    notifyFailed,
    notifySaved,
    notifySaving,
    registerRetry,
    setListing,
  ]);

  const saveFields = useCallback(
    (values: Record<string, any>, options: SaveFieldsOptions = {}) => {
      const { debounce = false, listingPatch } = options;

      const changed = pickChangedValues({
        ...toListingShape(values),
        ...listingPatch,
      });

      // Nothing to send. Leave any pending timer alone - it may still hold a
      // real change from another field.
      if (!Object.keys(changed).length) return;

      pendingRef.current = { ...pendingRef.current, ...changed };

      clearTimeout(debounceRef.current);

      if (debounce)
        debounceRef.current = setTimeout(flush, PREVIEW_SAVE_DEBOUNCE_TIME);
      else flush();
    },
    [flush, pickChangedValues, toListingShape],
  );

  const flushNow = useCallback(() => {
    clearTimeout(debounceRef.current);
    flush();
  }, [flush]);

  const notifyUploadStart = useCallback(() => notifySaving(), [notifySaving]);
  const notifyUploadSuccess = useCallback(() => notifySaved(), [notifySaved]);
  // No retry handler: re-sending an upload needs the original File, which is
  // not part of our payload. Dropzone surfaces its own error.
  const notifyUploadFailure = useCallback(() => notifyFailed(), [notifyFailed]);

  useEffect(
    () => () => {
      clearTimeout(debounceRef.current);
      registerRetry(null);
    },
    [registerRetry],
  );

  const value = useMemo(
    () => ({
      saveFields,
      flushNow,
      notifyUploadStart,
      notifyUploadSuccess,
      notifyUploadFailure,
    }),
    [
      saveFields,
      flushNow,
      notifyUploadStart,
      notifyUploadSuccess,
      notifyUploadFailure,
    ],
  );

  /**
   * ListingSlugField patches through PatchListingContext, which the form view
   * mounts below this point. Re-providing it keeps the slug saving in preview
   * mode.
   *
   * Scoped to the preview route on purpose. This provider sits above the
   * form/preview split, and while the create flow's own PatchListingProvider
   * shadows it, the edit flow mounts none - so an unscoped value would silently
   * switch edit form view from no autosave to autosave on blur.
   */
  const patchListingValue = useMemo(
    () => ({
      patchListingValue: isPreviewMode
        ? (values: Record<string, any>, updatedValues?: Partial<Listing>) =>
            saveFields(values, { listingPatch: updatedValues })
        : utils.noop,
    }),
    [isPreviewMode, saveFields],
  );

  return (
    <PreviewPatchListingContext.Provider value={value}>
      <PatchListingContext.Provider value={patchListingValue}>
        {children}
      </PatchListingContext.Provider>
    </PreviewPatchListingContext.Provider>
  );
};

export default PreviewPatchListingProvider;
